<template>
  <div class="content">
    <div class="header d-flex align-items-center profile-header">
      <!-- Mask -->
      <span class="mask bg-gradient-info"></span>
      <!-- Header container -->
      <div class="container-fluid d-flex align-items-center">
        <div class="row">
          <div class="col-lg-12">
            <router-link :to="{ name: 'LeadDetails', params: { id: leadId }}">
              <h1 class="display-3 text-white">{{ lead.first_name }} {{lead.last_name}}</h1>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $route.meta && $route.meta.name ? $route.meta.name : $route.name }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-breadcrumb></route-breadcrumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col-xl-7">
          <template>
            <card>
              <div slot="header" class="row align-items-center">
                <div class="col-8">
                  <h3 class="mb-0" id="lead_details_heading">
                    Lead Details
                    <badge
                      type="warning" class="ml-2" v-if="lead.is_unsubscribed" >
                      <span
                        @click="resubscribe"
                        @mouseover="unsubscribeText = 'Re-Subscribe User?'"
                        @mouseleave="unsubscribeText = 'Unsubscribed'">{{unsubscribeText}}
                      </span>
                    </badge>
                    <badge type="success" class="ml-2" v-else >Subscribed</badge>
                  </h3>
                </div>
                <div class="col-4 text-right">
                  <a href="javascript:" v-if="!isEditing" class="btn btn-sm btn-danger" @click="deleteLead">
                    Delete
                  </a>
                  <a href="javascript:" v-if="!isEditing" class="btn btn-sm btn-primary lead-edit" @click="enterEditMode">Edit</a>
                  <a href="javascript:" v-if="isEditing" class="btn btn-sm btn-primary lead-save" @click="saveEditedLead">Save</a>
                  <a href="javascript:" v-if="isEditing" class="btn btn-sm btn-danger lead-cancel" @click="cancelEditMode">Cancel</a>
                </div>
              </div>
              <form v-loading="loading" id="leadinformation">
                <div v-if="lead.first_name">
                  <h6 class="heading-small text-muted mb-4">
                    Lead Information
                  </h6>
                  <div>
                    <div class="row">
                      <div class="col-lg-6">
                        <base-input
                          :disabled="leadInputDisabled"
                          type="text"
                          label="First Name"
                          id="lead_information_first_name"
                          placeholder="First Name"
                          v-model="lead.first_name"
                        >
                        </base-input>
                      </div>
                      <div class="col-lg-6">
                        <base-input
                          :disabled="leadInputDisabled"
                          type="text"
                          label="Last Name"
                          id="lead_information_last_name"
                          placeholder="Last Name"
                          v-model="lead.last_name"
                        >
                        </base-input>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-lg-6">
                        <base-input
                          :disabled="leadInputDisabled"
                          type="email"
                          label="Email Address"
                          id="lead_information_email"
                          placeholder="mike@email.com"
                          v-model="lead.email"
                        >
                        </base-input>
                      </div>
                      <div class="col-lg-6">
                        <base-input
                          :disabled="leadInputDisabled"
                          type="phone"
                          id="lead_information_phone"
                          placeholder="0400 123 123"
                          v-model="lead.phone"
                        >
                        <template slot="label">
                          <label class="form-control-label">
                            Phone Number
                            <button class="btn btn-primary btn-sm ml-3" @click.stop.prevent="modalManagePhoneNumbers=true">Manage</button>
                          </label>
                        </template>
                        </base-input>
                        
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-lg-6">
                        <div class="label font-weight-bold">Country</div>
                        <el-select v-model="lead.country" :disabled="leadInputDisabled">
                            <el-option
                                v-for="(country, index) in ['AU', 'UK']"
                                :key="`choose_country_${index}_${country}`"
                                :label="country"
                                :value="country"
                            >
                            </el-option>
                        </el-select>
                      </div>
                      <div class="col-lg-6">
                        <div class="label font-weight-bold">Company</div>
                        <el-select 
                          v-model="selectedCompanyId" 
                          :disabled="leadInputDisabled"
                          filterable
                          remote
                          reserve-keyword
                          :placeholder="lead.company ? lead.company : 'Search or select a company...'"
                          :remote-method="searchCompanies"
                          :loading="searchingCompanies"
                          clearable
                          @clear="handleCompanyDeselect"
                          class="w-100"
                        >
                          <!-- Show current company if exists and not in search results -->
                          <el-option
                            v-if="lead.company && !companySearchResults.find(c => c.id === lead.company_id)"
                            :key="lead.company_id"
                            :label="lead.company"
                            :value="lead.company_id"
                          >
                            <div>
                              <div class="font-weight-bold">{{ lead.company }}</div>
                              <small class="text-muted" v-if="lead.company_industry">{{ lead.company_industry }}</small>
                            </div>
                          </el-option>
                          <!-- Search results -->
                          <el-option
                            v-for="company in companySearchResults"
                            :key="company.id"
                            :label="company.company_name"
                            :value="company.id"
                          >
                            <div>
                              <div class="font-weight-bold">{{ company.company_name }}</div>
                              <small class="text-muted" v-if="company.industry">{{ company.industry }}</small>
                            </div>
                          </el-option>
                        </el-select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-lg-6" v-if="lead.secondary_email">
                        <base-input
                          :disabled="leadInputDisabled"
                          type="email"
                          label="Secondary Email Address"
                          id="lead_information_secondary_email"
                          placeholder="mike@email.com"
                          v-model="lead.secondary_email"
                        >
                        </base-input>
                      </div>
                      <div class="col-lg-6"  v-if="lead.secondary_phone">
                        <base-input
                          :disabled="leadInputDisabled"
                          type="phone"
                          label="Secondary Phone Number"
                          id="lead_information_secondary_phone"
                          placeholder="0400 123 123"
                          v-model="lead.secondary_phone"
                        >
                        </base-input>
                      </div>
                    </div>
                  </div>
                </div>
                <hr class="my-4" />
                <h6 class="heading-small text-muted mb-4">Enquiries</h6>
                <enquiries :lead-id="leadId"></enquiries>
              </form>
              <lead-emails :lead-id="leadId"></lead-emails>
            </card>
          </template>
        </div>
        <div class="col-xl-5">
          <lead-events :lead-id="leadId"></lead-events>
        </div>
      </div>
    </div>
    <modal
        v-if="modalManagePhoneNumbers"
        :show.sync="modalManagePhoneNumbers"
        body-classes="p-0"
        modal-classes="modal-dialog-centered modal-lg"
      >
        <ManagePhoneNumbers :lead="lead" @cancel="modalManagePhoneNumbers = false"/>
    </modal>
    
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  MessageBox,
  Loading,
  Option,
  Select,
  FormItem
} from "element-ui";

import LeadEmails from '@/views/Widgets/LeadDetails/LeadEmails';
import Enquiries from '@/views/Widgets/LeadDetails/Enquiries';
import LeadEvents from '@/views/Widgets/LeadDetails/LeadEvents';
import ManagePhoneNumbers from '@/views/Widgets/LeadDetails/ManagePhoneNumbers';
import { mapState } from "vuex";
import LeadService from '@/services/lead.service';
import CompanyService from '@/services/company.service';

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    LeadEmails,
    Enquiries,
    LeadEvents,
    ManagePhoneNumbers,
    [FormItem.name]: FormItem,
  },
  data() {
    return {
      isEditing: false,
      leadId: parseInt(this.$route.params.id),
      dateOptions: {
        format: "YYYY-MM-DD",
      },
      loading: true,
      oldEmail: '',
      unsubscribeText: 'Unsubscribed',
      event_date : "",
      modalManagePhoneNumbers: false,
      // Company-related data
      companySearchResults: [],
      searchingCompanies: false,
      selectedCompanyId: null,
      oldCompanyId: null,
      searchTimeout: null
    };
  },
  async created() {
    await this.$store.dispatch("leads/getSingle", { id: this.leadId });
    if (this.lead?.country && this.country !== this.lead.country) {
      await this.$store.dispatch("country/setCountry", this.lead.country);
    }
    // Initialize company selection
    if (this.lead?.company_id) {
      this.selectedCompanyId = this.lead.company_id;
      this.oldCompanyId = this.lead.company_id;
    }
    this.loading = false;
  },
  computed: {
    lead() {
      return this.$store.getters["leads/getLeadById"](this.leadId);
    },
    ...mapState("country/", {
      country: (state) => state.country,
    }),
    leadInputDisabled() {
      return this.isEditing ? false : true;
    },
  },
  methods: {
    resubscribe() {
      MessageBox.confirm('Are you sure?', 'Re-Subscribe', {
        distinguishCancelAndClose: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }).then(async () => {
        this.loading = true;
        await LeadService.resubscribe(this.leadId).catch((error) => {
          this.$store.dispatch('alert/error', error, { root: true });
        });
        await this.$store.dispatch("leads/getSingle", { id: this.leadId });
        this.loading = false;
      });
    },
    deleteLead() {
      MessageBox.prompt(
        "Please type `DELETE` in the input box and press Delete",
        "Are you sure to delete this lead?",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          inputPattern: /^DELETE$/,
          inputErrorMessage: "Only accept `DELETE` word",
        }
      )
        .then(({ value }) => {
          let loadingInstance = Loading.service({ fullscreen: true });
          this.$store.dispatch("leads/deleteLead", this.leadId).then(() => {
            loadingInstance.close();
            this.$router.push({ name: "AllLeads" });
          });
        })
        .catch((err) => {
          //console.log(err);
        });
    },
    async shouldConfirmForRequeue() {
      MessageBox.confirm('Would you like to queue this lead to perform all lead actions?', 'Requeue', {
        distinguishCancelAndClose: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }).then(async () => {
        this.loading = true;
        await LeadService.requeue(this.leadId).catch((error) => {
          this.$store.dispatch('alert/error', error, { root: true });
        });
        this.loading = false;
      });
    },
    async saveEditedLead() {
      this.loading = true;
      let leadDetails = await LeadService.updateLead({
        first_name: this.lead.first_name,
        last_name: this.lead.last_name,
        email: this.lead.email,
        phone: this.lead.phone,
        event_type: this.lead.event_type,
        country: this.lead.country
      }, this.leadId).catch((error) => {
        if (error && error.response && error.response.status === 422) {
          this.$store.dispatch('alert/error', error.response.data.error.message, { root: true });
        } else {
          this.$store.dispatch('alert/error', error, { root: true });
        }
        return [];
      });

      // Handle company association changes
      if (this.selectedCompanyId !== this.oldCompanyId) {
        try {
          // Remove old association if exists
          if (this.oldCompanyId) {
            await this.$store.dispatch('companies/removeLeadAssociation', {
              company_id: this.oldCompanyId,
              lead_id: this.leadId
            });
          }

          // Add new association if selected
          if (this.selectedCompanyId) {
            await this.$store.dispatch('companies/associateLeads', {
              company_id: this.selectedCompanyId,
              lead_ids: [this.leadId]
            });
          }

          // Update old company ID for next edit
          this.oldCompanyId = this.selectedCompanyId;
        } catch (error) {
          this.$store.dispatch('alert/error', 'Failed to update company association: ' + error.message, { root: true });
        }
      }

      this.loading = false;
      if (leadDetails && leadDetails.data && leadDetails.data.id) {
        this.$store.dispatch('alert/success', "Lead updated successfully.", { root: true });
        if (this.oldEmail !== leadDetails.data.email) {
          this.shouldConfirmForRequeue();
        }
        // Refresh lead data to get updated company info
        await this.$store.dispatch("leads/getSingle", { id: this.leadId });
      }
      this.isEditing = false;
    },
    async searchCompanies(query) {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }

      if (!query || query.length <= 2) {
        // Don't clear existing results if we have a currently selected company
        if (!this.selectedCompanyId || this.selectedCompanyId === null) {
          this.companySearchResults = [];
        }
        this.searchingCompanies = false;
        return;
      }
      
      // Set loading state
      this.searchingCompanies = true;
      
      // Debounce the search request
      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await CompanyService.searchCompanies({
            query: query
          });
          
          this.companySearchResults = response.data || [];
        } catch (error) {
          console.error('Failed to search companies:', error);
          this.$store.dispatch('alert/error', 'Failed to search companies', { root: true });
          this.companySearchResults = [];
        } finally {
          this.searchingCompanies = false;
          this.searchTimeout = null;
        }
      }, 500);
    },
    handleCompanyDeselect() {
      this.selectedCompanyId = null;
    },
    // Handle entering edit mode
    enterEditMode() {
      this.isEditing = true;
      this.oldEmail = this.lead.email;
      this.oldCompanyId = this.selectedCompanyId;
    },
    // Handle cancelling edit mode
    cancelEditMode() {
      // Reset company selection to original value
      this.selectedCompanyId = this.oldCompanyId;
      this.isEditing = false;
    },
  },
  beforeDestroy() {
    // Clean up timeout when component is destroyed
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }
};
</script>

<style scoped>
.btn {
  color: white;
}
.btn:hover {
  color: white;
}
.label {
    font-size: 14px;
    margin-bottom: 8px;
}
</style>

